import { useId, useRef, useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

// Shared Monday-first control; inlineCalendar avoids nested popup interception in dialogs.
const splitValue = (value, withTime) => {
  if (!value) return { date: '', time: withTime ? '08:00' : '' };
  const [d, t = ''] = String(value).split('T');
  return { date: d, time: withTime ? (t.slice(0, 5) || '08:00') : '' };
};
const toDate = (iso) => {
  if (!iso) return undefined;
  const parsed = parse(iso, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : undefined;
};
export const isValidDateInput = (value) => typeof value === 'string'
  && /^\d{4}-\d{2}-\d{2}$/.test(value) && Boolean(toDate(value));

export default function DateField({
  value, onChange, min, max, withTime = false, disabled = false,
  className = '', placeholder = 'Pick a date', dark = false,
  allowTextEntry = false, inlineCalendar = false, 'data-testid': testId,
}) {
  const [open, setOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const uniqueId = useId();
  const triggerRef = useRef(null);
  const controlId = testId || `date-field-${uniqueId.replace(/:/g, '')}`;
  const { date, time } = splitValue(value, withTime);
  const selected = toDate(date);
  const validSyntax = isValidDateInput(date);
  const invalid = allowTextEntry && Boolean(date) && (!validSyntax || (min && date < min) || (max && date > max));
  const emit = (nextDate, nextTime) => {
    if (!nextDate) return onChange('');
    onChange(withTime ? `${nextDate}T${nextTime || '08:00'}` : nextDate);
  };
  const pick = (day) => {
    if (!day) return;
    emit(format(day, 'yyyy-MM-dd'), time);
    setOpen(false);
    if (inlineCalendar) requestAnimationFrame(() => triggerRef.current?.focus());
  };
  const trigger = (
    <Button ref={triggerRef} type="button" variant="outline" disabled={disabled}
      className={cn(allowTextEntry ? 'w-10 h-10 p-0 shrink-0' : 'flex-1 justify-start font-normal min-w-0', !date && 'text-slate-400')}
      aria-label={allowTextEntry ? `${open ? 'Close' : 'Open'} date calendar` : undefined}
      title={allowTextEntry ? 'Choose date from calendar' : undefined}
      aria-expanded={open} aria-controls={inlineCalendar ? `${controlId}-panel` : undefined}
      onClick={inlineCalendar ? () => { setCalendarMonth(selected || new Date()); setOpen((prev) => !prev); } : undefined}
      data-testid={controlId}>
      <CalendarIcon size={14} className={cn('shrink-0 opacity-60', !allowTextEntry && 'mr-2')} />
      {!allowTextEntry && <span className="truncate">{selected ? format(selected, 'EEE, MMM d, yyyy') : placeholder}</span>}
    </Button>
  );
  const calendar = (
    <Calendar mode="single" weekStartsOn={1} selected={selected} defaultMonth={selected}
      {...(inlineCalendar ? { month: calendarMonth, onMonthChange: setCalendarMonth } : {})}
      onSelect={pick} disabled={(day) => {
        const iso = format(day, 'yyyy-MM-dd');
        return disabled || Boolean(min && iso < min) || Boolean(max && iso > max);
      }} initialFocus data-testid={`${controlId}-calendar`} />
  );
  return (
    <div className={cn('flex items-center gap-2 min-w-0', (allowTextEntry || inlineCalendar) && 'flex-wrap', className)}>
      {allowTextEntry && <Input type="text" value={date} placeholder="YYYY-MM-DD" aria-label="Assignment date (YYYY-MM-DD)" autoComplete="off" disabled={disabled}
        aria-invalid={!!invalid} aria-describedby={invalid ? `${controlId}-error` : undefined}
        onChange={(e) => { const next = e.target.value.trim(); emit(next, time); if (inlineCalendar && isValidDateInput(next)) setCalendarMonth(toDate(next)); }}
        className="min-w-0 flex-1 font-mono" data-testid={`${controlId}-input`} />}
      {inlineCalendar ? trigger : <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className={cn('w-auto max-w-[calc(100vw-1rem)] p-0 z-[200]', dark && 'dark')} align={allowTextEntry ? 'end' : 'start'} data-testid={`${controlId}-popover`}>
          {calendar}
        </PopoverContent>
      </Popover>}
      {withTime && <Input type="time" value={time} disabled={disabled}
        onChange={(e) => emit(date || format(new Date(), 'yyyy-MM-dd'), e.target.value)}
        className="w-[7.5rem] shrink-0" data-testid={`${controlId}-time`} />}
      {invalid && <p id={`${controlId}-error`} role="alert" className="basis-full text-xs text-red-600" data-testid={`${controlId}-error`}>
        {!validSyntax ? 'Enter a valid date as YYYY-MM-DD.' : min && date < min ? `Choose ${min} or later.` : `Choose ${max} or earlier.`}
      </p>}
      {inlineCalendar && open && <div id={`${controlId}-panel`} className={cn('basis-full min-w-0 flex justify-center rounded-md border border-slate-200 bg-white', dark && 'dark bg-neutral-900')} data-testid={`${controlId}-calendar-panel`}>
        {calendar}
      </div>}
    </div>
  );
}